import React, {useMemo} from 'react';
import {
    Box,
    Stack,
    TextField,
    Typography,
    MenuItem,
    Checkbox
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import {generateUUID} from '../../../utility/uuidUtil';
import IconButton from '@mui/material/IconButton';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import EmailsInput from '../../../shared-components/EmailsInput';
import {DatePicker} from '@mui/x-date-pickers';
import moment from 'moment';
import CancelIcon from '@mui/icons-material/Close';
import useNotification from '../../../utility/hooks/useNotification';
import useTextField from '../../../utility/hooks/useTextField';
import {validateFieldLength} from '../../../utility/validationUtil';

export const getFormUrl = (formId) => `${window.origin}/forms-request/${formId}`;

export const getIFrameHtml = (formId, frameId) => `
<iframe src="${getFormUrl(formId)}/?embedded=true"
        id='${frameId}'
        frameborder="0"
        marginheight="0"
        marginwidth="0"
        width="100%">
</iframe>
<script>
    function iframeResizeListener(event) {
        if (event.data && event.data.type === 'iframeResizer' && event.data.iframeHeight) {
            let iframe = document.getElementById('${frameId}');
            iframe.style.height = event.data.iframeHeight + 'px';
        }
    }
    window.addEventListener('message', iframeResizeListener, false);
</script>`;

export const getIFrame = (formId) =>
    getIFrameHtml(formId, generateUUID())
        .replace(/[\n\r\t]+|\s\s+/g, ' ')
        .trim();

const PublicFormSettings = ({
                                formId,
                                notificationEmails,
                                onNotificationEmailsChange,
                                expirationDate,
                                onExpirationDateChange,
                                isFormArchived,
                                name,
                                onNameChange,
                                categories,
                                onCategoriesChange,
                                formCategories
                            }) => {
    const iframe = useMemo(() => getIFrame(formId), [formId]);
    const formUrl = getFormUrl(formId);

    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification
    } = useNotification();

    const nameField = useTextField({
        initialValue: name ?? '',
        validate: (value) => validateFieldLength(value, 128),
        onChange: (event) => onNameChange(event.target.value)
    });

    const copyToClipBoard = async (src, label) => {
        try {
            await navigator.clipboard.writeText(src);
            showSuccessNotification(`${label} copied to clipboard`);
        } catch (e) {
            if (showDetailedErrorNotification) {
                showDetailedErrorNotification(
                    'Unable to copy to clipboard',
                    e?.message || 'Please copy the value manually.'
                );
            } else if (showErrorNotification) {
                showErrorNotification('Unable to copy to clipboard');
            }
        }
    };

    const selectedCategories = categories || [];

    const handleCategoryChange = (e) => {
        onCategoriesChange(e.target.value);
    };

    const handleClearExpiration = () => {
        onExpirationDateChange(null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 720,
                    p: 3
                }}
            >
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Form details
                        </Typography>
                        <Stack spacing={2.5}>
                            <TextField
                                fullWidth
                                label="Form Name"
                                required={nameField.required}
                                disabled={isFormArchived}
                                value={nameField.value}
                                onChange={nameField.onChange}
                                error={nameField.error}
                                helperText={nameField.helperText || ' '}
                                autoComplete="off"
                            />

                            <TextField
                                select
                                fullWidth
                                disabled={isFormArchived}
                                value={selectedCategories}
                                label="Categories"
                                helperText=" "
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => {
                                        if (!Array.isArray(selected) || selected.length === 0) {
                                            return <em>No categories</em>;
                                        }

                                        const selectedCats =
                                            formCategories?.filter(c =>
                                                selected.includes(c.id)
                                            ) || [];

                                        return (
                                            <Stack
                                                flexDirection="row"
                                                gap={1}
                                                flexWrap="wrap"
                                            >
                                                {selectedCats.map(cat => (
                                                    <Box
                                                        key={cat.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.8,
                                                            padding: 1,
                                                            borderRadius: '18px',
                                                            border: '1px solid #adadad'
                                                        }}
                                                    >
                                                        <CircleIcon
                                                            fontSize="small"
                                                            sx={{color: cat.color}}
                                                        />
                                                        <Typography variant="body2">
                                                            {cat.name}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        );
                                    },
                                    MenuProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                whiteSpace: 'unset',
                                                wordBreak: 'break-all'
                                            }
                                        }
                                    }
                                }}
                                onChange={handleCategoryChange}
                            >
                                {formCategories?.map(c => (
                                    <MenuItem key={c.id} value={c.id}>
                                        <Checkbox checked={selectedCategories.includes(c.id)}/>
                                        <CircleIcon
                                            fontSize="small"
                                            sx={{
                                                color: c.color,
                                                mr: 1
                                            }}
                                        />
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Notifications
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Add the email addresses that should be notified when this form is
                            submitted.
                        </Typography>
                        <EmailsInput
                            disabled={isFormArchived}
                            label="Notification email addresses"
                            values={notificationEmails}
                            onChange={emails =>
                                onNotificationEmailsChange(
                                    Array.from(
                                        new Map(emails.map(item => [item.email, item])).values()
                                    )
                                )
                            }
                            onBlur={email =>
                                onNotificationEmailsChange(prev =>
                                    Array.from(
                                        new Map([
                                            ...prev.map(item => [item.email, item]),
                                            [email, {email}]
                                        ]).values()
                                    )
                                )
                            }
                        />
                    </Box>

                    <Box maxWidth={360}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Expiration date
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Choose when this form should no longer accept responses.
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Box sx={{flexGrow: 1}}>
                                <DatePicker
                                    label="Expiration date (optional)"
                                    disabled={isFormArchived}
                                    value={expirationDate ? moment(expirationDate) : null}
                                    onChange={(v) => {
                                        if (!v) return onExpirationDateChange(null);

                                        const endOfDayUtc = v
                                            .clone()
                                            .utc()
                                            .endOf('day');

                                        onExpirationDateChange(endOfDayUtc.toISOString());
                                    }}
                                    timezone="UTC"
                                    format="MMM D, YYYY"
                                    minDate={moment()}
                                    disablePast
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            helperText: 'Expires at 23:59 (UTC) on this date',
                                            inputProps: {readOnly: true}
                                        }
                                    }}
                                />
                            </Box>

                            {expirationDate && (
                                <IconButton
                                    disabled={isFormArchived}
                                    aria-label="Clear expiration date"
                                    onClick={handleClearExpiration}
                                    size="small"
                                    sx={{
                                        transform: 'translateY(-10px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CancelIcon fontSize="small"/>
                                </IconButton>
                            )}
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Public link
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Share this URL directly with people who need to complete the form.
                        </Typography>
                        <Stack
                            direction={{
                                xs: 'column',
                                sm: 'row'
                            }}
                            alignItems={{
                                xs: 'stretch',
                                sm: 'center'
                            }}
                            spacing={1.5}
                        >
                            <TextField
                                fullWidth
                                value={formUrl}
                                label="Form URL"
                                variant="outlined"
                                InputProps={{readOnly: true}}
                                size="small"
                            />
                            <Tooltip title="Copy to clipboard">
                                <IconButton
                                    onClick={() => copyToClipBoard(formUrl, 'URL')}
                                    sx={{
                                        alignSelf: {
                                            xs: 'flex-end',
                                            sm: 'center'
                                        },
                                        mt: {
                                            xs: 0.5,
                                            sm: 0
                                        }
                                    }}
                                >
                                    <ContentCopyRoundedIcon/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>

                    <Box sx={{pb: 4}}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Embed on your website
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Use this embed code to display the form inside another website.
                        </Typography>
                        <Stack
                            direction={{
                                xs: 'column',
                                sm: 'row'
                            }}
                            alignItems={{
                                xs: 'stretch',
                                sm: 'flex-start'
                            }}
                            spacing={1.5}
                        >
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={6}
                                value={iframe}
                                label="Embed code"
                                variant="outlined"
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily:
                                            'Monaco, Menlo, Consolas, "Courier New", monospace',
                                        fontSize: 12,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{
                                    '& .MuiInputLabel-outlined': {
                                        backgroundColor: '#fff',
                                        paddingRight: 1,
                                    },
                                }}
                            />

                            <Tooltip title="Copy to clipboard">
                                <IconButton
                                    onClick={() => copyToClipBoard(iframe, 'Embed code')}
                                    sx={{
                                        alignSelf: {
                                            xs: 'flex-end',
                                            sm: 'flex-start'
                                        },
                                        mt: {
                                            xs: 0.5,
                                            sm: 0
                                        }
                                    }}
                                >
                                    <ContentCopyRoundedIcon/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

PublicFormSettings.propTypes = {
    formId: PropTypes.number,
    notificationEmails: PropTypes.array,
    onNotificationEmailsChange: PropTypes.func,
    expirationDate: PropTypes.string,
    onExpirationDateChange: PropTypes.func,
    isFormArchived: PropTypes.bool,
    name: PropTypes.string,
    onNameChange: PropTypes.func,
    categories: PropTypes.array,
    onCategoriesChange: PropTypes.func,
    formCategories: PropTypes.array,
};

export default PublicFormSettings;
